// The "full breakdown" overlay behind the final-standings screen's button.
// The itemised half of the standings screen: the balance-sheet table
// (cash / stocks / properties / houses) and, per player, the
// property-by-property detail of what they owned and what it earned. The
// game-wide recap table stays on the standings screen itself.
import { useState } from "react"
import classNames from "classnames"
import {
  ChartBarIcon,
  ChevronDownIcon,
  TrophyIcon,
} from "@heroicons/react/24/solid"
import Modal from "@/Components/Modal"
import Money from "@/Components/Money"
import { allPieces } from "@/utils/data"
import {
  WORTH_SEGMENTS,
  type EndGamePlayerWorth,
} from "@/utils/endGameBreakdown"

interface EndGameBreakdownModalProps {
  sheets: EndGamePlayerWorth[]
  /** The situation's players, for the piece icons. */
  players: IPlayer[]
  winnerOrder?: number | null
  onClose: () => void
}

const cellClass = "px-3 py-2 text-right tabular-nums"

// Money renders as a grid whose last column eats the free space, so it only
// right-aligns when it sits in a flex row that lets it shrink to its content.
const MoneyValue: React.FC<{ value: number; className?: string }> = ({
  value,
  className,
}) => (
  <div className="flex justify-end">
    <Money
      money={value.toLocaleString()}
      className={classNames("gap-1 tabular-nums", className)}
    />
  </div>
)

const EndGameBreakdownModal: React.FC<EndGameBreakdownModalProps> = ({
  sheets,
  players,
  winnerOrder,
  onClose,
}) => {
  // Open by default when there are only a couple of rows; beyond that the table
  // stays scannable until a row is asked for.
  const [expanded, setExpanded] = useState<PlayerOrder[]>(
    sheets.length <= 2 ? sheets.map((sheet) => sheet.order) : []
  )

  const toggle = (order: PlayerOrder) =>
    setExpanded((current) =>
      current.includes(order)
        ? current.filter((item) => item !== order)
        : [...current, order]
    )

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      // Only a click on the backdrop itself closes — not one that bubbled up
      // out of the panel (expanding a row, selecting text in the tables).
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <Modal
        data-testid="modal.end-game-breakdown"
        className="relative flex max-h-[92vh] w-full max-w-[86rem] flex-col"
        onClose={onClose}
        icon={<ChartBarIcon className="h-5 w-5 text-white" />}
        title="Full Breakdown"
        subtitle="Where every net worth came from"
        headerSize="sm"
        contentClassName="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-6"
      >
        <div className="flex min-h-0 flex-col gap-5">
          <div className="overflow-hidden rounded-2xl border border-gray-700/60 bg-black/25">
            <table className="w-full border-collapse font-poppins text-sm">
              <thead>
                <tr className="bg-white/5 text-[0.65rem] uppercase tracking-wider text-white/40">
                  <th className="px-4 py-3 text-left font-medium">Player</th>
                  {WORTH_SEGMENTS.map((segment) => (
                    <th
                      key={segment.key}
                      className="px-3 py-3 text-right font-medium"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: segment.color }}
                        />
                        {segment.label}
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-right font-medium">
                    Rent Earned
                  </th>
                  <th className="px-3 py-3 text-right font-medium">Net Worth</th>
                  <th className="w-8 px-2 py-3" />
                </tr>
              </thead>
              <tbody>
                {sheets.map((sheet) => {
                  const isWinner = sheet.order === winnerOrder
                  const isOpen = expanded.includes(sheet.order)
                  const piece = allPieces.find(
                    (item) =>
                      item.id ===
                      players.find((player) => player.order === sheet.order)
                        ?.pieceId
                  )

                  return [
                    <tr
                      key={sheet.order}
                      onClick={() => toggle(sheet.order)}
                      className={classNames(
                        "cursor-pointer border-t border-white/5 transition-colors hover:bg-white/5",
                        { "bg-amber-400/10": isWinner }
                      )}
                    >
                      <td className="px-4 py-2 text-left">
                        <div className="flex items-center gap-2">
                          {piece?.imgSrc && (
                            <img
                              src={piece.imgSrc}
                              alt=""
                              className={classNames(
                                "h-6 w-6 shrink-0 object-contain",
                                { grayscale: sheet.isBankrupt }
                              )}
                            />
                          )}
                          <span
                            className={classNames("truncate", {
                              "font-semibold text-amber-200": isWinner,
                              "text-white/80": !isWinner && !sheet.isBankrupt,
                              "text-white/40": sheet.isBankrupt,
                            })}
                          >
                            {sheet.name || `Player ${sheet.order}`}
                          </span>
                          {isWinner && (
                            <TrophyIcon className="h-4 w-4 shrink-0 text-amber-300" />
                          )}
                          {sheet.isBankrupt && (
                            <span className="shrink-0 rounded-md bg-red-500/20 px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wide text-red-300">
                              Bankrupt
                            </span>
                          )}
                        </div>
                      </td>

                      <td className={classNames(cellClass, "text-white/80")}>
                        <MoneyValue value={sheet.cash} />
                      </td>
                      <td className={classNames(cellClass, "text-white/80")}>
                        <MoneyValue value={sheet.stockValue} />
                        <div className="text-[0.65rem] text-white/40">
                          {sheet.stockShares.toLocaleString()} shares
                        </div>
                      </td>
                      <td className={classNames(cellClass, "text-white/80")}>
                        <MoneyValue value={sheet.propertyValue} />
                        <div className="text-[0.65rem] text-white/40">
                          {sheet.propertyCount} owned
                        </div>
                      </td>
                      <td className={classNames(cellClass, "text-white/80")}>
                        <MoneyValue value={sheet.houseValue} />
                        <div className="text-[0.65rem] text-white/40">
                          {sheet.houseCount} house
                          {sheet.houseCount === 1 ? "" : "s"}
                          {sheet.hotelCount > 0 &&
                            `, ${sheet.hotelCount} hotel${
                              sheet.hotelCount === 1 ? "" : "s"
                            }`}
                        </div>
                      </td>

                      <td className={classNames(cellClass, "text-emerald-300")}>
                        <MoneyValue value={sheet.rentEarned} />
                        <div className="text-[0.65rem] text-white/40">
                          spent M{sheet.spentOnProperties.toLocaleString()}
                        </div>
                      </td>

                      <td className={cellClass}>
                        <MoneyValue
                          value={sheet.totalWorth}
                          className={classNames(
                            "text-base font-bold",
                            isWinner ? "text-yellow-300" : "text-white"
                          )}
                        />
                      </td>

                      <td className="px-2 py-2 text-right">
                        <ChevronDownIcon
                          className={classNames(
                            "h-4 w-4 text-white/40 transition-transform",
                            { "rotate-180": isOpen }
                          )}
                        />
                      </td>
                    </tr>,

                    isOpen && (
                      <tr
                        key={`${sheet.order}-detail`}
                        className={classNames({ "bg-amber-400/5": isWinner })}
                      >
                        <td colSpan={8} className="px-4 pb-4 pt-1">
                          {sheet.properties.length === 0 ? (
                            <div className="rounded-xl border border-white/5 bg-black/25 px-3 py-2 text-xs text-white/40">
                              No properties owned.
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                              {sheet.properties.map((property) => (
                                <div
                                  key={property.order}
                                  className="flex items-center gap-2 rounded-xl border border-white/5 bg-black/25 px-3 py-2"
                                >
                                  <span
                                    className="h-8 w-1.5 shrink-0 rounded-full"
                                    style={{
                                      backgroundColor:
                                        property.color || "#64748b",
                                    }}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="truncate text-xs text-white/80">
                                        {property.name}
                                      </span>
                                      {property.mortgaged && (
                                        <span className="shrink-0 rounded bg-red-500/20 px-1 text-[0.6rem] uppercase text-red-300">
                                          Mtg
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[0.65rem] text-white/40">
                                      M{property.price.toLocaleString()}
                                      {property.houses > 0 &&
                                        ` + ${
                                          property.isHotel
                                            ? "hotel"
                                            : `${property.houses} house${
                                                property.houses === 1 ? "" : "s"
                                              }`
                                        } M${property.houseValue.toLocaleString()}`}
                                    </div>
                                  </div>
                                  <div className="shrink-0 text-right">
                                    <div className="text-[0.6rem] uppercase tracking-wide text-white/40">
                                      Rent
                                    </div>
                                    <div
                                      className={classNames(
                                        "text-xs font-semibold tabular-nums",
                                        property.rentEarned > 0
                                          ? "text-emerald-300"
                                          : "text-white/40"
                                      )}
                                    >
                                      M{property.rentEarned.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ),
                  ]
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default EndGameBreakdownModal
